import type { DetailFileField } from '@zelos/codegen/apis';
import type { FC, InputHTMLAttributes, Ref, RefCallback } from 'react';
import { useId, useCallback, useEffect, useMemo, useState, forwardRef, useRef } from 'react';
import type { ChangeHandler } from 'react-hook-form';
import { fileDownloadByUrl, getSingleFile } from './file-util';
import { mergedRef } from './merged-ref';

interface SingleFileInputHocProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  fileInServer?: DetailFileField | null;
  deleteFileInServerOnClear?: boolean;
  error?: string;
  onChange: ChangeHandler;
}

export interface SingleFileInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  error?: string;
  fileName: string | null;
  className?: string;
  fileInServerInUse: boolean;
  inputId: string;
  clearFile: () => void;
  downloadFileInServer: () => void;
}

export const asSingleFileInput = <T extends object>(WrappedComponent: FC<SingleFileInputProps>) => {
  const WrappedInput = ({
    fileInServer,
    deleteFileInServerOnClear,
    onChange,
    error,
    className,
    onBlur,
    name,
    min,
    max,
    maxLength,
    minLength,
    pattern,
    required,
    disabled,
    accept,
    ...property
  }: SingleFileInputHocProps & T, forwardedRef: Ref<HTMLInputElement> | RefCallback<HTMLInputElement>) => {
    const innerRef = useRef<HTMLInputElement | null>(null);
    const inputRef = useMemo(
      () => mergedRef([forwardedRef, innerRef]),
      [forwardedRef, innerRef],
    );

    const [fileName, setFileName] = useState<string | null>(null);
    const [fileInServerInUse, setFileInServerInUse] = useState(!!fileInServer);

    const beforeInputValue = useRef<string | null>(null);

    const inputId = useId();

    const clearFile = useCallback(() => {
      const inputElem = innerRef.current;
      if (!inputElem) return;

      inputElem.value = '';
      setFileName('');
      setFileInServerInUse(false);
      onChange?.({
        target: {
          name: inputElem.name,
          value: ((fileInServer && deleteFileInServerOnClear) || !fileInServer) ? '' : null,
        },
      });
    }, [
      deleteFileInServerOnClear,
      fileInServer,
      onChange,
    ]);

    const downloadFileInServer = useCallback(() => {
      fileInServerInUse
      && fileInServer
      && fileDownloadByUrl(fileInServer.url, fileInServer.name);
    }, [fileInServer, fileInServerInUse]);

    useEffect(() => {
      fileInServerInUse
      && setFileName(fileInServer?.name ?? '');
    }, [fileInServer, fileInServerInUse]);

    useEffect(() => {
      fileInServer
        ? setFileInServerInUse(true)
        : clearFile();
    }, [fileInServer, clearFile]);

    useEffect(() => {
      disabled
      && fileInServer
      && setFileInServerInUse(true);
    }, [disabled, fileInServer]);

    useEffect(() => {
      const inputElem = innerRef.current;
      if (!inputElem) return;

      onChange && setTimeout(() => {
        onChange({
          target: {
            name: inputElem.name,
            value: null,
          },
        });
      });
    }, [onChange]);

    useEffect(() => {
      const inputElem = innerRef.current;
      if (!inputElem) return;

      const currentValue = inputElem.value;

      !currentValue
      && beforeInputValue.current
      && clearFile();

      beforeInputValue.current = currentValue;

    }, [innerRef.current?.value, clearFile]);

    return (
      <>
        <WrappedComponent
          fileName={fileName}
          clearFile={clearFile}
          downloadFileInServer={downloadFileInServer}
          error={error}
          className={className}
          fileInServerInUse={fileInServerInUse}
          inputId={inputId}
          disabled={disabled}
          {...property}
        />
        <input
          id={inputId}
          type="file"
          ref={inputRef}
          style={{
            display: 'none',
          }}
          accept={accept}
          onBlur={onBlur}
          name={name}
          min={min}
          max={max}
          maxLength={maxLength}
          minLength={minLength}
          pattern={pattern}
          required={required}
          disabled={disabled}
          onChange={(event) => {
            const file = getSingleFile(event.target.files);
            setFileName(file?.name ?? '');
            setFileInServerInUse(false);
            onChange?.({
              target: {
                name: event.target.name,
                value: file,
              },
            });
          }}
        />
      </>
    );
  };

  return forwardRef(WrappedInput);
};
